import { useEffect, useState } from "react";
import { Sparkles, Info, Zap, Loader2, Check, Copy, MessageCircle, Upload } from "lucide-react";
import api from "@/lib/api";
import { useConfig } from "@/contexts/ConfigContext";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

/**
 * Barra de uso de Colega IA — usada por AsistenteIA y Membership.
 * Consume GET /api/ai/quota. Sólo abogados con membresía activa reciben 200.
 * Muestra base (30) + extras acumulados por packs, y ofrece comprar un pack
 * de 20 consultas por 0.25 UF cuando el usuario se acerca al límite.
 *
 * Props:
 *   compact: bool
 *   refreshKey: any → forzar refetch tras enviar consulta
 *   onPackPurchased: () => void → callback tras registrar la compra
 */
export default function AIQuotaMeter({ compact = false, refreshKey = 0, onPackPurchased }) {
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [planMeta, setPlanMeta] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);
  const [buyErr, setBuyErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/ai/quota")
      .then(({ data }) => { if (alive) setQ(data); })
      .catch((e) => { if (alive) setErr(e?.response?.status || "err"); });
    return () => { alive = false; };
  }, [refreshKey]);

  // Trae metadatos de /plans (UF + precio del pack en CLP) — silencioso.
  useEffect(() => {
    let alive = true;
    api.get("/plans")
      .then(({ data }) => { if (alive) setPlanMeta(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (err) return null;
  if (!q) return null;

  const total = q.quota || 0;
  const used = q.used || 0;
  const base = q.base_quota ?? total;
  const extras = q.extras ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const remaining = q.remaining ?? Math.max(0, total - used);
  const nearLimit = pct >= 80;
  const exhausted = remaining === 0;
  const packSize = q.extra_pack_size || planMeta?.ai_extra_pack_queries || 20;
  const packUf = planMeta?.ai_extra_pack_uf || 0.25;
  const packClp = planMeta?.ai_extra_pack_clp || 10200;

  const buyPack = async () => {
    setBuying(true); setBuyErr("");
    try {
      const { data } = await api.post("/ai/extra-pack/purchase");
      setBuyResult(data);
      onPackPurchased && onPackPurchased(data);
    } catch (e) {
      setBuyErr(e?.response?.data?.detail || "No se pudo registrar la compra.");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div
      className={`panel ${compact ? "p-4" : "p-5"} border-cyan-400/25 bg-cyan-400/[0.03]`}
      data-testid="ai-quota-meter"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={compact ? 14 : 16} className="text-cyan-300" />
          <span className={`${compact ? "text-xs" : "text-sm"} font-medium`}>Consultas Colega IA</span>
        </div>
        <div className="text-xs text-muted-foreground" data-testid="ai-quota-counter">
          <span className={nearLimit ? "text-amber-300 font-semibold" : "text-foreground font-semibold"}>{used}</span>
          <span> / {total} este mes</span>
          {extras > 0 && <span className="ml-1 text-emerald-300">(+{extras} pack)</span>}
        </div>
      </div>

      <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${
            exhausted ? "bg-gradient-to-r from-red-500 to-red-400" :
            nearLimit ? "bg-gradient-to-r from-amber-400 to-red-400" :
            "bg-gradient-to-r from-cyan-400 to-emerald-400"
          }`}
          style={{ width: `${pct}%` }}
          data-testid="ai-quota-bar"
        />
      </div>

      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
        <Info size={11} />
        {exhausted ? (
          <span className="text-red-300 font-medium" data-testid="ai-quota-exhausted-msg">
            Agotaste tus {base} consultas del mes{extras > 0 ? ` + ${extras} pack` : ""}. Compra un pack extra o espera al 1 del próximo mes.
          </span>
        ) : nearLimit ? (
          <span className="text-amber-200">Te quedan sólo <span className="text-foreground font-medium">{remaining}</span> consultas. Considera reservar un pack extra.</span>
        ) : (
          <>Te quedan <span className="text-foreground font-medium">{remaining}</span> consultas para el mes {q.year_month}.</>
        )}
      </div>
      {!compact && (
        <div className="mt-2 text-[11px] text-cyan-100/75" data-testid="ai-quota-session-copy">
          {q.consultation_definition || "Una consulta incluye todas las aclaraciones hasta tu respuesta final."}
        </div>
      )}

      {(nearLimit || exhausted) && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
            exhausted
              ? "bg-amber-500 hover:bg-amber-400 text-amber-950"
              : "border border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
          }`}
          data-testid="ai-buy-pack-btn"
        >
          <Zap size={12}/> Comprar pack extra · {packSize} consultas por {CLP(packClp)}
        </button>
      )}

      {modalOpen && (
        <BuyPackModal
          onClose={() => { setModalOpen(false); setBuyResult(null); setBuyErr(""); }}
          onConfirm={buyPack}
          buying={buying}
          buyErr={buyErr}
          buyResult={buyResult}
          packSize={packSize}
          packUf={packUf}
          packClp={packClp}
        />
      )}
    </div>
  );
}

function BuyPackModal({ onClose, onConfirm, buying, buyErr, buyResult, packSize, packUf, packClp }) {
  const { config } = useConfig();
  const bank = config?.bank_account || {};
  const wa = (config?.admin_whatsapp || "").replace(/[^+\d]/g, "");
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const ref = (buyResult?.pack_id || "").slice(0, 8).toUpperCase();
  const plainText = buyResult ? [
    `Pack extra Colega IA · ${packSize} consultas`,
    `Referencia (OBLIGATORIA): ${ref}`,
    `Monto: ${CLP(buyResult.amount_clp)}`,
    "",
    `Banco: ${bank.bank_name}`,
    `Titular: ${bank.holder}`,
    `RUT: ${bank.holder_rut}`,
    `${bank.account_type}: ${bank.account_number}`,
    `Email para comprobante: ${bank.email}`,
  ].join("\n") : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(plainText); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* noop */ }
  };

  const whatsappUrl = buyResult && wa
    ? `https://wa.me/${wa.replace(/\+/, "")}?text=${encodeURIComponent(
        `Hola, adjunto comprobante por ${CLP(buyResult.amount_clp)} del pack extra Colega IA. Referencia: ${ref}`
      )}`
    : null;

  const handleFile = async (file) => {
    if (!file || !buyResult) return;
    if (file.size > 5 * 1024 * 1024) { setUploadErr("El archivo supera los 5 MB."); return; }
    setUploading(true); setUploadErr("");
    try {
      const b64 = await new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(String(r.result || "").split(",").pop()); r.onerror = ko; r.readAsDataURL(file); });
      // Reutilizamos el mismo endpoint de upload de comprobante (pack extra usa
      // colección `ai_extra_packs`, pero para el MVP guardamos el archivo en
      // notifications si no calza — para simplicidad usamos endpoint dedicado):
      await api.post(`/ai/extra-pack/${buyResult.pack_id}/upload-receipt`, {
        file_b64: b64, file_name: file.name, mime_type: file.type,
      });
      setUploaded(true);
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || e.message || "No se pudo subir.");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="buy-pack-modal">
      <div className="panel max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-1"><Zap size={11}/> Pack Extra Colega IA</div>
            <h3 className="serif text-2xl mt-1">{packSize} consultas adicionales</h3>
            <div className="text-xs text-muted-foreground mt-1">Válidas para el mes en curso · se acreditan al aprobar el pago</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="buy-pack-close">✕</button>
        </div>

        {buyResult ? (
          <div className="mt-6 space-y-3" data-testid="buy-pack-transfer">
            {/* Referencia + monto */}
            <div className="p-4 rounded-lg border border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-amber-900/20">
              <div className="text-[10px] label-eyebrow text-amber-300">Referencia obligatoria</div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <div className="serif text-2xl tracking-widest text-amber-200 font-bold">{ref}</div>
                <div className="serif text-xl text-emerald-300">{CLP(buyResult.amount_clp)}</div>
              </div>
              <div className="mt-2 text-[10px] text-amber-200/80">⚠️ Sin esta referencia no podemos identificar tu pago.</div>
            </div>

            {/* Datos bancarios */}
            <div className="p-3 rounded-md bg-white/5 border border-white/10 text-xs space-y-1 font-mono">
              <PackRow label="Banco" value={bank.bank_name}/>
              <PackRow label="Titular" value={bank.holder}/>
              <PackRow label="RUT" value={bank.holder_rut}/>
              <PackRow label={bank.account_type || "Cuenta"} value={bank.account_number}/>
              <PackRow label="Email comprobante" value={bank.email}/>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={copy} className="px-3 py-2 rounded-md border border-white/15 text-xs hover:bg-white/5 inline-flex items-center justify-center gap-2">
                {copied ? <><Check size={12} className="text-emerald-300"/>Copiado</> : <><Copy size={12}/> Copiar datos</>}
              </button>
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md text-xs bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium inline-flex items-center justify-center gap-2">
                  <MessageCircle size={12}/> WhatsApp
                </a>
              )}
            </div>

            {/* Upload comprobante */}
            <div className="p-3 rounded-md border border-dashed border-cyan-400/30 bg-cyan-400/[0.03]">
              <div className="text-[10px] label-eyebrow text-cyan-300 inline-flex items-center gap-1"><Upload size={10}/> Subir comprobante (opcional)</div>
              {!uploaded ? (
                <>
                  <input type="file" accept="image/*,application/pdf" disabled={uploading}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="mt-2 block w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-white/10 file:text-foreground hover:file:bg-white/15 cursor-pointer disabled:opacity-50"/>
                  {uploadErr && <div className="mt-1.5 text-xs text-red-300">{uploadErr}</div>}
                  {uploading && <div className="mt-1.5 text-xs text-cyan-300">Subiendo…</div>}
                </>
              ) : (
                <div className="mt-2 text-xs text-emerald-300 inline-flex items-center gap-1.5">
                  <Check size={12}/> Comprobante recibido. Aprobaremos tu pack al confirmar el depósito.
                </div>
              )}
            </div>

            <button onClick={onClose} className="btn-primary w-full" data-testid="buy-pack-done">Listo, ya voy a transferir</button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="p-4 rounded-md bg-white/5 border border-white/10 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Cantidad</span><span className="font-medium">{packSize} consultas</span></div>
              <div className="flex justify-between items-baseline pt-2 border-t border-white/5">
                <span className="text-xs text-muted-foreground">Total a transferir</span>
                <span className="serif text-2xl text-amber-300">{CLP(packClp)}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Transferencia manual — te enviaremos los datos y activaremos el pack cuando el equipo confirme el pago.</p>
            {buyErr && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="buy-pack-err">{buyErr}</div>}
            <button onClick={onConfirm} disabled={buying} className="btn-primary w-full disabled:opacity-50" data-testid="buy-pack-confirm">
              {buying ? <><Loader2 className="animate-spin" size={14}/> Registrando…</> : "Registrar compra"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PackRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground font-medium text-right">{value || "—"}</span>
    </div>
  );
}
